import React, { useState, useEffect, useContext, useRef } from 'react';
import axios from 'axios';
import { API, AuthContext } from '@/App';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { Avatar } from './Avatar';
import { ROLE_OPTIONS, EXTERNAL_ROLES, EMPLOYEE_SUBTYPES, LANGUAGES, TIME_ZONES, roleType } from './options';
import { copyToClipboard, formatPhone } from './util';
import { Upload, Trash2, Lock, Unlock, KeyRound, Copy } from 'lucide-react';

const Field = ({ label, children, hint }) => (
  <div className="space-y-1.5">
    <Label className="text-xs font-semibold text-slate-600">{label}</Label>
    {children}
    {hint && <p className="text-[11px] text-slate-400">{hint}</p>}
  </div>
);

const nativeSelect =
  'w-full rounded-md border border-gray-200 px-3 py-2 text-sm text-slate-700 bg-white focus:outline-none focus:ring-2 focus:ring-[#8FC93A]/40';

const UserForm = ({ open, onClose, onSaved, editUser, markets, users, customers, locations }) => {
  const { token, user: me } = useContext(AuthContext);
  const authHeaders = { headers: { Authorization: `Bearer ${token}` } };
  const isOwner = me?.role === 'owner';
  const isEdit = !!editUser;
  const fileRef = useRef(null);
  const [avatarBump, setAvatarBump] = useState(0);

  const blank = {
    full_name: '', email: '', role: '', job_title: '', employee_subtype: '',
    mobile_number: '', office_number: '', supervisor_id: '', service_footprint: [],
    preferred_language: 'English', time_zone: 'America/New_York',
    customer_id: '', assigned_location_ids: [],
    password: '', auto_generate_password: true, must_change_password: true,
  };
  const [form, setForm] = useState(blank);
  const [userType, setUserType] = useState(null); // 'Internal' | 'External' — first structural choice
  const [saving, setSaving] = useState(false);
  const [genPassword, setGenPassword] = useState(null);

  useEffect(() => {
    if (open) {
      setGenPassword(null);
      if (editUser) {
        setUserType(editUser.user_type || (EXTERNAL_ROLES.includes(editUser.role) ? 'External' : 'Internal'));
        setForm({
          ...blank,
          ...editUser,
          full_name: editUser.full_name || editUser.name || '',
          service_footprint: editUser.service_footprint || [],
          assigned_location_ids: editUser.assigned_location_ids || [],
          supervisor_id: editUser.supervisor_id || '',
          customer_id: editUser.customer_id || '',
        });
      } else {
        setUserType(null);
        setForm(blank);
      }
    }
  }, [open, editUser]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const isExternal = userType === 'External';
  const availableRoles = ROLE_OPTIONS.filter((r) => r.type === userType && (!r.ownerOnly || isOwner));
  const internalUsers = (users || []).filter((u) => roleType(u.role) === 'Internal' && u.id !== editUser?.id);
  const customerLocations = (locations || []).filter((l) => l.customer_id === form.customer_id);

  const chooseType = (t) => {
    setUserType(t);
    const firstRole = ROLE_OPTIONS.find((r) => r.type === t && (!r.ownerOnly || isOwner));
    set('role', firstRole ? firstRole.value : '');
  };

  const toggleMarket = (id) => {
    set('service_footprint', form.service_footprint.includes(id)
      ? form.service_footprint.filter((m) => m !== id)
      : [...form.service_footprint, id]);
  };
  const toggleLocation = (id) => {
    set('assigned_location_ids', form.assigned_location_ids.includes(id)
      ? form.assigned_location_ids.filter((m) => m !== id)
      : [...form.assigned_location_ids, id]);
  };

  const submit = async () => {
    if (!form.full_name.trim()) return toast.error('Full name is required');
    if (!isEdit && !form.email.trim()) return toast.error('Email is required');
    if (isExternal && !form.customer_id) return toast.error('External/client users must be assigned to a Customer Account');
    if (isExternal && form.role === 'client_location_requestor' && form.assigned_location_ids.length === 0) return toast.error('Assign at least one Service Location');
    setSaving(true);
    try {
      if (isEdit) {
        const payload = {
          name: form.full_name, full_name: form.full_name, role: form.role,
          job_title: form.job_title,
          employee_subtype: isExternal ? null : form.employee_subtype,
          mobile_number: form.mobile_number, office_number: form.office_number,
          supervisor_id: isExternal ? null : (form.supervisor_id || null),
          service_footprint: isExternal ? [] : form.service_footprint,
          preferred_language: form.preferred_language, time_zone: form.time_zone,
          customer_id: isExternal ? (form.customer_id || null) : null,
          assigned_location_ids: isExternal ? form.assigned_location_ids : [],
        };
        await axios.put(`${API}/admin/users/${editUser.id}`, payload, authHeaders);
        toast.success('User updated');
        onSaved();
        onClose();
      } else {
        const payload = {
          email: form.email, name: form.full_name, full_name: form.full_name, role: form.role,
          user_type: userType,
          auto_generate_password: form.auto_generate_password,
          password: form.auto_generate_password ? null : form.password,
          must_change_password: form.must_change_password,
          job_title: form.job_title,
          employee_subtype: isExternal ? null : form.employee_subtype,
          mobile_number: form.mobile_number, office_number: form.office_number,
          supervisor_id: isExternal ? null : (form.supervisor_id || null),
          service_footprint: isExternal ? [] : form.service_footprint,
          preferred_language: form.preferred_language, time_zone: form.time_zone,
          customer_id: isExternal ? (form.customer_id || null) : null,
          assigned_location_ids: isExternal ? form.assigned_location_ids : [],
        };
        const r = await axios.post(`${API}/admin/users`, payload, authHeaders);
        onSaved();
        if (r.data?.generated_password) {
          setGenPassword(r.data.generated_password);
          toast.success('User created — copy the temporary password');
        } else {
          toast.success('User created');
          onClose();
        }
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not save user');
    } finally {
      setSaving(false);
    }
  };

  const uploadPhoto = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append('file', file);
    try {
      await axios.post(`${API}/admin/users/${editUser.id}/photo`, fd, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' },
      });
      toast.success('Photo updated');
      setAvatarBump((b) => b + 1);
      onSaved();
      editUser.photo_url = 'set';
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Photo upload failed');
    }
  };

  const removePhoto = async () => {
    try {
      await axios.delete(`${API}/admin/users/${editUser.id}/photo`, authHeaders);
      toast.success('Photo removed');
      editUser.photo_url = null;
      setAvatarBump((b) => b + 1);
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not remove photo');
    }
  };

  const toggleLock = async () => {
    try {
      await axios.patch(`${API}/admin/users/${editUser.id}/photo-lock`,
        { photo_locked: !editUser.photo_locked }, authHeaders);
      editUser.photo_locked = !editUser.photo_locked;
      toast.success(editUser.photo_locked ? 'Photo locked' : 'Photo unlocked');
      onSaved();
      setAvatarBump((b) => b + 1);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not change lock');
    }
  };

  const photoLocked = editUser?.photo_locked;
  const canEditPhoto = !photoLocked || isOwner;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="user-form-dialog">
        <DialogHeader>
          <DialogTitle className="text-[#0E2A47]">
            {isEdit ? `Edit ${editUser.full_name || editUser.name}` : 'Create User'}
            {userType && <Badge variant="outline" className="ml-2 align-middle text-[10px]">{userType}</Badge>}
          </DialogTitle>
        </DialogHeader>

        {genPassword ? (
          <div className="py-4" data-testid="generated-password-panel">
            <div className="rounded-lg border border-[#E8C99B] bg-[#FBF3E4] p-5 text-center">
              <KeyRound className="mx-auto text-[#C8641E] mb-2" size={28} />
              <p className="text-sm text-slate-600 mb-2">Temporary password — share it securely. It won't be shown again here.</p>
              <div className="flex items-center justify-center gap-2">
                <code className="rounded bg-white px-4 py-2 font-mono text-lg font-bold text-[#0E2A47] border" data-testid="generated-password-value">{genPassword}</code>
                <button onClick={() => copyToClipboard(genPassword)}
                  className="rounded-md border border-gray-200 p-2 hover:bg-gray-50" data-testid="copy-password-btn"><Copy size={16} /></button>
              </div>
            </div>
            <DialogFooter className="mt-5">
              <button onClick={onClose} className="rounded-md bg-[#0E2A47] px-5 py-2 text-sm font-semibold text-white" data-testid="password-done-btn">Done</button>
            </DialogFooter>
          </div>
        ) : !userType ? (
          <div className="py-6" data-testid="user-type-step">
            <p className="text-center text-sm text-slate-500 mb-5">First, choose the type of user you're creating.</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <button onClick={() => chooseType('Internal')} data-testid="user-type-internal"
                className="rounded-xl border-2 border-gray-200 p-6 text-left hover:border-[#0E2A47] hover:bg-gray-50 transition-colors">
                <div className="text-base font-bold text-[#0E2A47]">Internal — TFS User</div>
                <p className="mt-1 text-xs text-slate-500">Administrators & field personnel who work for Total Facility Solutions.</p>
              </button>
              <button onClick={() => chooseType('External')} data-testid="user-type-external"
                className="rounded-xl border-2 border-gray-200 p-6 text-left hover:border-[#5FA718] hover:bg-[#5FA718]/5 transition-colors">
                <div className="text-base font-bold text-[#0E2A47]">External — Client User</div>
                <p className="mt-1 text-xs text-slate-500">Client contacts (Facilities Directors & Location Requestors) tied to one Customer.</p>
              </button>
            </div>
          </div>
        ) : (
          <div className="space-y-5 py-2">
            {!isEdit && (
              <button onClick={() => setUserType(null)} className="text-xs font-semibold text-slate-500 hover:text-[#0E2A47]" data-testid="user-type-back">
                ← {userType === 'External' ? 'External — Client User' : 'Internal — TFS User'} · change type
              </button>
            )}
            {/* Photo (edit only) */}
            {isEdit && (
              <div className="flex items-center gap-4 rounded-lg border border-gray-100 bg-gray-50/60 p-4">
                <Avatar userId={editUser.id} hasPhoto={!!editUser.photo_url} name={form.full_name} size={64} bump={avatarBump} />
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-semibold text-slate-700">Profile Photo</span>
                    {photoLocked && <Badge className="bg-[#0E2A47] text-white text-[10px]"><Lock size={10} className="mr-1" />Locked</Badge>}
                  </div>
                  <div className="mt-2 flex flex-wrap gap-2">
                    <input ref={fileRef} type="file" accept="image/*" className="hidden"
                      onChange={(e) => uploadPhoto(e.target.files?.[0])} data-testid="photo-file-input" />
                    <button disabled={!canEditPhoto} onClick={() => fileRef.current?.click()} data-testid="photo-upload-btn"
                      className="inline-flex items-center gap-1.5 rounded-md border border-gray-200 px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-white disabled:opacity-40">
                      <Upload size={13} /> {editUser.photo_url ? 'Replace' : 'Upload'}
                    </button>
                    {editUser.photo_url && (
                      <button disabled={!canEditPhoto} onClick={removePhoto} data-testid="photo-remove-btn"
                        className="inline-flex items-center gap-1.5 rounded-md border border-red-200 px-3 py-1.5 text-xs font-semibold text-red-600 hover:bg-red-50 disabled:opacity-40">
                        <Trash2 size={13} /> Remove
                      </button>
                    )}
                    <button onClick={toggleLock} disabled={photoLocked && !isOwner} data-testid="photo-lock-btn"
                      className="inline-flex items-center gap-1.5 rounded-md border border-gray-200 px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-white disabled:opacity-40">
                      {photoLocked ? <><Unlock size={13} /> Unlock</> : <><Lock size={13} /> Lock</>}
                    </button>
                  </div>
                  {photoLocked && !isOwner && <p className="mt-1.5 text-[11px] text-slate-400">Locked — only a Global Administrator can change or unlock this photo.</p>}
                </div>
              </div>
            )}

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field label="Full Name *">
                <Input value={form.full_name} onChange={(e) => set('full_name', e.target.value)} data-testid="uf-fullname" />
              </Field>
              <Field label="Email *" hint={isEdit ? 'Email cannot be changed' : undefined}>
                <Input type="email" value={form.email} disabled={isEdit}
                  onChange={(e) => set('email', e.target.value)} data-testid="uf-email" />
              </Field>
              <Field label="Role">
                <select className={nativeSelect} value={form.role} onChange={(e) => set('role', e.target.value)} data-testid="uf-role">
                  {availableRoles.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
                </select>
              </Field>
              {!isExternal && (
                <Field label="Employee Subtype">
                  <Input list="subtype-list" value={form.employee_subtype || ''}
                    onChange={(e) => set('employee_subtype', e.target.value)} placeholder="e.g. Route Technician" data-testid="uf-subtype" />
                  <datalist id="subtype-list">{EMPLOYEE_SUBTYPES.map((s) => <option key={s} value={s} />)}</datalist>
                </Field>
              )}
              <Field label="Job Title">
                <Input value={form.job_title || ''} onChange={(e) => set('job_title', e.target.value)} data-testid="uf-jobtitle" />
              </Field>
              <Field label="Mobile Number">
                <Input value={form.mobile_number || ''} onChange={(e) => set('mobile_number', e.target.value)}
                  onBlur={(e) => set('mobile_number', formatPhone(e.target.value))} placeholder="(305) 555-1212" data-testid="uf-mobile" />
              </Field>
              <Field label="Office Number">
                <Input value={form.office_number || ''} onChange={(e) => set('office_number', e.target.value)}
                  onBlur={(e) => set('office_number', formatPhone(e.target.value))} placeholder="(305) 555-1212" data-testid="uf-office" />
              </Field>
              {!isExternal && (
                <Field label="Supervisor">
                  <select className={nativeSelect} value={form.supervisor_id || ''} onChange={(e) => set('supervisor_id', e.target.value)} data-testid="uf-supervisor">
                    <option value="">— None —</option>
                    {internalUsers.map((u) => <option key={u.id} value={u.id}>{u.full_name || u.name}</option>)}
                  </select>
                </Field>
              )}
              <Field label="Time Zone">
                <select className={nativeSelect} value={form.time_zone} onChange={(e) => set('time_zone', e.target.value)} data-testid="uf-timezone">
                  {TIME_ZONES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                </select>
              </Field>
            </div>

            {/* Service footprint — internal only */}
            {!isExternal && (
            <Field label="Service Footprint (Markets)">
              <div className="flex flex-wrap gap-2" data-testid="uf-markets">
                {(markets || []).filter((m) => m.active).map((m) => {
                  const on = form.service_footprint.includes(m.id);
                  return (
                    <button key={m.id} type="button" onClick={() => toggleMarket(m.id)}
                      data-testid={`uf-market-${m.id}`}
                      className={`rounded-full px-3 py-1 text-xs font-medium border transition-colors ${on ? 'bg-[#0E2A47] text-white border-[#0E2A47]' : 'bg-white text-slate-600 border-gray-200 hover:bg-gray-50'}`}>
                      {m.name}
                    </button>
                  );
                })}
              </div>
            </Field>
            )}

            {/* External-only: customer + locations */}
            {isExternal && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 rounded-lg border border-blue-100 bg-blue-50/40 p-4">
                <Field label="Customer *" hint="External users are scoped to one customer">
                  <select className={nativeSelect} value={form.customer_id || ''}
                    onChange={(e) => { set('customer_id', e.target.value); set('assigned_location_ids', []); }} data-testid="uf-customer">
                    <option value="">— Select customer —</option>
                    {(customers || []).map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                </Field>
                {form.role === 'client_location_requestor' && form.customer_id && (
                  <Field label="Assigned Locations">
                    <div className="max-h-32 overflow-y-auto rounded-md border border-gray-200 bg-white p-2 space-y-1" data-testid="uf-locations">
                      {customerLocations.map((l) => (
                        <label key={l.id} className="flex items-center gap-2 text-xs text-slate-700 cursor-pointer">
                          <input type="checkbox" checked={form.assigned_location_ids.includes(l.id)}
                            onChange={() => toggleLocation(l.id)} data-testid={`uf-loc-${l.id}`} />
                          {l.location_name}
                        </label>
                      ))}
                      {customerLocations.length === 0 && <p className="text-[11px] text-slate-400">No locations for this customer.</p>}
                    </div>
                  </Field>
                )}
              </div>
            )}

            {/* Password (create only) */}
            {!isEdit && (
              <div className="rounded-lg border border-gray-100 bg-gray-50/60 p-4 space-y-3">
                <div className="flex items-center gap-2 text-sm font-semibold text-slate-700"><KeyRound size={15} /> Temporary Password</div>
                <div className="flex items-center gap-2">
                  <Switch checked={form.auto_generate_password} onCheckedChange={(v) => set('auto_generate_password', v)} data-testid="uf-autogen-switch" />
                  <span className="text-sm text-slate-600">Auto-generate a secure password</span>
                </div>
                {!form.auto_generate_password && (
                  <Input type="text" placeholder="Enter a temporary password" value={form.password}
                    onChange={(e) => set('password', e.target.value)} data-testid="uf-password" />
                )}
                <label className="flex items-center gap-2 text-sm text-slate-600 cursor-pointer">
                  <input type="checkbox" checked={form.must_change_password}
                    onChange={(e) => set('must_change_password', e.target.checked)} data-testid="uf-mustchange" />
                  Remind user to change password on next login (non-blocking)
                </label>
              </div>
            )}

            <DialogFooter className="gap-2">
              <button onClick={onClose} className="rounded-md border border-gray-200 px-5 py-2 text-sm font-semibold text-slate-600 hover:bg-gray-50" data-testid="uf-cancel">Cancel</button>
              <button onClick={submit} disabled={saving} className="rounded-md bg-[#0E2A47] px-5 py-2 text-sm font-semibold text-white hover:bg-[#16324F] disabled:opacity-50" data-testid="uf-submit">
                {saving ? 'Saving…' : isEdit ? 'Save Changes' : 'Create User'}
              </button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default UserForm;
